<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { mloc } from "module/utils";
  import { collectionStores } from "module/store/CollectionStore";
  import { enabledDocumentTypes } from "module/store/IndexingDisabledStore";

  import FolderTreeItem from "./FolderTreeItem.svelte";
  import {
    FILTER_FOLDER_ROOT,
    type SearchFilterConfig,
  } from "module/store/Filters";
  import FilterModal from "./FilterModal.svelte";
  import FilterOptionSausage from "./FilterOptionSausage.svelte";

  let { open = false, config }: { open?: boolean; config: SearchFilterConfig } =
    $props();

  let folderFilter = $state("");

  const folders = collectionStores.folders;

  const dispatch = createEventDispatcher();

  function buildTree(
    folders?: Folder[],
    parent: Folder | null = null
  ): FolderTree[] {
    if (!folders) return [];

    const [children, rest] = folders.reduce(
      ([p, f], e) => (e.folder === parent ? [[...p, e], f] : [p, [...f, e]]),
      [[] as Folder[], [] as Folder[]]
    );

    return children.map((f) => ({
      folder: f,
      selected: Boolean(
        config.folders[0] === FILTER_FOLDER_ROOT ||
          (f.id && config.folders.includes(f.id))
      ),
      children: buildTree(rest, f).sort(
        f.sorting === "a"
          ? (a, b) => a.folder.name?.localeCompare(b.folder.name || "") || 0
          : (a, b) => a.folder.sort - b.folder.sort
      ),
    }));
  }

  function sortByType(tree: FolderTree[]) {
    const baseTypes = new Set(
      config.documentTypes.map((type) => type.split(":")[0])
    );
    const filterTypes = Boolean(baseTypes.size);

    const byType = Object.fromEntries(
      $enabledDocumentTypes
        .filter((type) => !filterTypes || baseTypes.has(type))
        .map((t) => [t, [] as FolderTree[]])
    );

    tree.forEach((node) => byType[node.folder.type]?.push(node));
    return Object.values(byType).flat();
  }

  function toggleAll() {
    if (!config.folders.length || config.folders[0] !== FILTER_FOLDER_ROOT) {
      dispatch("change", { folders: [FILTER_FOLDER_ROOT] });
    } else {
      dispatch("change", { folders: [] });
    }
  }

  function selectFolder(evt: CustomEvent) {
    let updated = new Set(config.folders);
    if (config.folders[0] === FILTER_FOLDER_ROOT) {
      updated = new Set([evt.detail]);
    } else if (config.folders.includes(evt.detail)) {
      updated.delete(evt.detail);
    } else {
      updated.add(evt.detail);
    }
    dispatch("change", { folders: Array.from(updated) });
  }

  function clear() {
    dispatch("change", { folders: [] });
  }

  let folderTree = $derived(
    folders && config && sortByType(buildTree($folders))
  );
</script>

<FilterModal
  title={mloc("FilterEditorFolderSelectTitle")}
  {open}
  on:close
  on:clear={clear}
>
  <div class="filter-input">
    <input
      type="text"
      placeholder={mloc("FilterEditorOptionFilter")}
      class="option-filter"
      bind:value={folderFilter}
    />
    <i
      class="fas fa-xmark clear {folderFilter ? '' : 'hide-clear'}"
      title={mloc("FilterEditorOptionFilterClear")}
      on:click={() => (folderFilter = "")}
    ></i>
  </div>

  <ul>
    <FilterOptionSausage
      selected={config?.folders[0] === FILTER_FOLDER_ROOT}
      title={mloc("FilterEditorFolderRoot")}
      square
      on:click={toggleAll}
    />
    {#if folderTree}
      {#each folderTree as node (node.folder.id)}
        <FolderTreeItem folderNode={node} on:selectFolder={selectFolder} />
      {/each}
    {/if}
  </ul>
</FilterModal>

<style>
  ul {
    overflow: auto;
    padding: 0;
    flex-grow: 1;
    margin: 0;
  }
  .filter-input {
    display: flex;
    align-items: center;
    position: relative;
  }

  .clear {
    position: absolute;
    right: 0;
    padding: 0 0.5em;
    height: 100%;
    display: flex;
    align-items: center;
  }
  .hide-clear {
    opacity: 0;
  }
  .option-filter {
    border-radius: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 0.6em !important;
    flex-grow: 1;
    color: var(--color-light-2) !important;
  }
  .option-filter::placeholder {
    color: var(--color-light-2);
    opacity: 0.6;
  }

  .option-filter:focus {
    box-shadow: rgba(0, 0, 0, 0.25) 0px 0px 0px 2px inset;
  }
</style>
